/**
 * Main Multidev Collaboration Hub Application Component
 * Task 5.7: SDK Entry Point
 *
 * Orchestrates the commenting overlay UI.
 * Integrates all SDK components with the collaboration provider.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import type { SDKConfig } from './index';
import type { Comment, ElementFingerprint, JiraFormFields } from './lib/types';
import { FAB } from './components/FAB';
import { CommentMode } from './components/comments/CommentMode';
import { CommentComposer } from './components/comments/CommentComposer';
import { PinManager, triggerPositionUpdateAnimation } from './components/comments/PinManager';
import { CommentThread } from './components/comments/CommentThread';
import { Sidebar } from './components/sidebar/Sidebar';
import { SignInPrompt } from './components/auth/SignInPrompt';
import { useCommentPositioning, createVirtualElement } from './hooks/useCommentPositioning';
import { tokens } from './styles/tokens';
import {
  MultidevCollaborationProvider,
  useMultidevCollaboration,
} from './context/MultidevCollaborationProvider';
import {
  AuthManagerProvider,
  useAuthManager,
} from './context/AuthManagerContext';
import { useToast } from './hooks/useToast';
import { ToastContainer } from './components/Toast';
import { getCommentDeepLink, copyToClipboard } from './utils/deepLinkUtils';
import { useDeepLinkNavigation } from './hooks/useDeepLinkNavigation';
import { getElementId } from './lib/thread-grouping';

interface MultidevCollaborationAppProps {
  config: SDKConfig;
}

/**
 * Inner app component WITHOUT Auth0 (for development when auth is disabled)
 */
const MultidevCollaborationAppInnerWithoutAuth: React.FC = () => {
  const collaboration = useMultidevCollaboration();
  return <AppUI {...collaboration} />;
};

/**
 * Inner app component WITH AuthManager authentication
 */
const MultidevCollaborationAppInner: React.FC = () => {
  const { authManager, isAuthenticated, isLoading: authLoading } = useAuthManager();
  const collaboration = useMultidevCollaboration();

  // Show loading state while checking authentication
  if (authLoading) {
    return null; // Or a loading spinner if desired
  }

  // Show sign-in prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div
        data-multidev-sdk="true"
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 999999,
          pointerEvents: 'auto',
        }}
      >
        <SignInPrompt
          authManager={authManager ?? undefined}
          compact
          message="Sign in to comment"
        />
      </div>
    );
  }

  return <AppUI {...collaboration} />;
};

/**
 * Main UI component (shared between auth and non-auth modes)
 */
const AppUI: React.FC<ReturnType<typeof useMultidevCollaboration>> = (collaboration) => {
  const {
    comments,
    teamMembers,
    commentsWithMatches,
    orphanedCommentIds,
    isLoading,
    hasNewComments,
    unreadCommentIds,
    currentPagePath,
    createComment,
    createReply,
    resolveComment,
    reopenComment,
    deleteComment,
    refreshComments,
    markAsRead,
    markAsUnread,
    // Jira integration
    jiraConnection,
    jiraTickets,
    generateJiraTicket,
    createJiraTicket,
    connectJira,
  } = collaboration;

  // Toast notifications
  const { toasts, showToast, dismissToast } = useToast();

  // Ref to access shadow root for DOM queries (SDK uses Shadow DOM)
  const containerRef = React.useRef<HTMLDivElement>(null);

  // UI State
  const [isCommentModeActive, setIsCommentModeActive] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedElement, setSelectedElement] = useState<Element | null>(null);
  const [selectedFingerprint, setSelectedFingerprint] = useState<ElementFingerprint | null>(null);
  const [composerPosition, setComposerPosition] = useState<{ top: number; left: number } | null>(null);
  const [expandedCommentId, setExpandedCommentId] = useState<string | null>(null);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
  const [highlightedCommentId, setHighlightedCommentId] = useState<string | null>(null);
  const [isLoadingJiraTicket, setIsLoadingJiraTicket] = useState(false);

  // Setup positioning hook for comment thread viewer
  const {
    refs: threadRefs,
    floatingStyles: threadFloatingStyles,
    isPositioned: isThreadPositioned,
  } = useCommentPositioning({
    isSidebarOpen,
    offsetDistance: 12,
    viewportPadding: 8,
    maxWidth: 480,
    maxHeight: 600,
  });

  // Setup positioning hook for composer
  const {
    refs: composerRefs,
    floatingStyles: composerFloatingStyles,
    isPositioned: isComposerPositioned,
  } = useCommentPositioning({
    isSidebarOpen,
    offsetDistance: 0,  // No offset - appear right at cursor
    viewportPadding: 20,
    maxWidth: 400,
    maxHeight: 300,
  });

  // Team members map for component props
  const teamMembersMap = useMemo(
    () => new Map(teamMembers.map(m => [m.id, m])),
    [teamMembers]
  );

  // Get Jira projects and issue types from provider
  const {
    jiraProjects,
    jiraIssueTypes,
    jiraProjectsLoading,
    fetchJiraProjects,
    fetchJiraIssueTypes,
    getCommentJiraTicket,
  } = collaboration;

  // Get replies for a parent comment
  const getReplies = useCallback((parentId: string) => {
    return comments.filter(c => c.threadId === parentId);
  }, [comments]);

  /**
   * Set sidebar open state with viewport push.
   * Applies margin-right to document to prevent sidebar from obscuring content.
   * Triggers pin position animation so pins smoothly follow their elements.
   */
  const setSidebarOpenWithViewportPush = useCallback((open: boolean) => {
    const html = document.documentElement;
    html.style.transition = `margin-right ${tokens.animation.durationMs}ms ${tokens.animation.timingRaw}`;
    html.style.marginRight = open ? tokens.layout.sidebarWidth : '0px';
    setIsSidebarOpen(open);
    // Trigger continuous pin position updates during the transition
    triggerPositionUpdateAnimation(tokens.animation.durationMs);
  }, []);

  // Handlers
  const toggleCommentMode = useCallback(() => {
    setIsCommentModeActive(prev => !prev);
    setSelectedElement(null);
    setSelectedFingerprint(null);
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarOpenWithViewportPush(!isSidebarOpen);
  }, [isSidebarOpen, setSidebarOpenWithViewportPush]);

  // Unified ESC key handler for all dismissible UI
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;

      // Priority order: expanded thread > composer > comment mode > sidebar
      if (expandedCommentId) {
        setExpandedCommentId(null);
      } else if (selectedElement) {
        setSelectedElement(null);
        setSelectedFingerprint(null);
        setComposerPosition(null);
      } else if (isCommentModeActive) {
        setIsCommentModeActive(false);
      } else if (isSidebarOpen) {
        setSidebarOpenWithViewportPush(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [expandedCommentId, selectedElement, isSidebarOpen, isCommentModeActive, setSidebarOpenWithViewportPush]);

  // Helper to fetch Jira ticket for a comment
  const fetchJiraTicketForComment = useCallback((commentId: string) => {
    // Skip if already cached
    if (jiraTickets.has(commentId)) {
      setIsLoadingJiraTicket(false);
      return;
    }

    setIsLoadingJiraTicket(true);
    getCommentJiraTicket(commentId)
      .catch((err) => {
        console.warn('[Multidev Collaboration] Failed to fetch Jira ticket:', err);
      })
      .finally(() => {
        setIsLoadingJiraTicket(false);
      });
  }, [jiraTickets, getCommentJiraTicket]);

  const handleElementSelect = useCallback((element: Element, fingerprint: ElementFingerprint, clickPosition: { x: number; y: number }) => {
    setSelectedElement(element);
    setSelectedFingerprint(fingerprint);
    setComposerPosition({ top: clickPosition.y, left: clickPosition.x });
    setIsCommentModeActive(false);

    // Create virtual element at cursor for positioning
    // Use setPositionReference for virtual elements (per Floating UI docs)
    const virtualElement = createVirtualElement(clickPosition.x, clickPosition.y);
    composerRefs.setPositionReference(virtualElement);
  }, [composerRefs]);

  const handleCancelComposer = useCallback(() => {
    setSelectedElement(null);
    setSelectedFingerprint(null);
    setComposerPosition(null);
  }, []);

  const handleSubmitComment = useCallback(async (data: { content: string }) => {
    if (!selectedElement) return;

    const result = await createComment(data.content, selectedElement);

    if (result) {
      setSelectedElement(null);
      setSelectedFingerprint(null);
      setComposerPosition(null);
    }
  }, [selectedElement, createComment]);

  const handlePinClick = useCallback((comment: Comment, pinElement: HTMLElement) => {
    const isOpening = expandedCommentId !== comment.id;
    setExpandedCommentId(prev => prev === comment.id ? null : comment.id);
    threadRefs.setReference(pinElement);
    setSelectedCommentId(comment.id);

    // When opening a comment thread
    if (isOpening) {
      // Mark as read (optimistic + fire-and-forget)
      markAsRead(comment.id);
      // Fetch Jira ticket
      fetchJiraTicketForComment(comment.id);
    }
  }, [threadRefs, expandedCommentId, markAsRead, fetchJiraTicketForComment]);

  const handleResolve = useCallback(async (comment: Comment) => {
    await resolveComment(comment.id);
  }, [resolveComment]);

  const handleReopen = useCallback(async (comment: Comment) => {
    await reopenComment(comment.id);
  }, [reopenComment]);

  const handleDelete = useCallback(async (comment: Comment) => {
    await deleteComment(comment.id);
    if (expandedCommentId === comment.id) {
      setExpandedCommentId(null);
    }
  }, [deleteComment, expandedCommentId]);

  const handleReAnchor = useCallback(async (_comment: Comment) => {
    // Enter re-anchor mode - user needs to select new element
    setIsCommentModeActive(true);
    // TODO: Implement re-anchor flow with element selection
  }, []);

  const handleCreateReply = useCallback(async (content: string, parentComment: Comment) => {
    await createReply(content, parentComment);
  }, [createReply]);

  const handleCloseThread = useCallback(() => {
    setExpandedCommentId(null);
    setIsLoadingJiraTicket(false);
  }, []);

  const handleCopyLink = useCallback(async (comment: Comment) => {
    const url = getCommentDeepLink(comment.id);
    const success = await copyToClipboard(url);
    if (success) {
      showToast('Link copied', 'success');
    } else {
      showToast('Failed to copy link', 'error');
    }
  }, [showToast]);

  // Jira handlers
  const handleGenerateJiraTicket = useCallback(async (comment: Comment) => {
    return await generateJiraTicket(comment);
  }, [generateJiraTicket]);

  const handleCreateJiraTicket = useCallback(async (comment: Comment, fields: JiraFormFields) => {
    return await createJiraTicket(comment, fields);
  }, [createJiraTicket]);

  const handleOpenJiraTicket = useCallback((ticket: import('./lib/types').JiraTicket) => {
    window.open(ticket.url, '_blank', 'noopener,noreferrer');
  }, []);

  // Get linked ticket for expanded comment
  const expandedCommentTicket = useMemo(() => {
    if (!expandedCommentId) return undefined;
    return jiraTickets.get(expandedCommentId) ?? undefined;
  }, [expandedCommentId, jiraTickets]);

  // Adapt jiraConnection to JiraConnection type for CommentThread
  const adaptedJiraConnection = useMemo(() => {
    if (!jiraConnection) return undefined;
    return {
      isConnected: true,
      siteUrl: jiraConnection.jiraSiteUrl,
      defaultProjectKey: jiraConnection.defaultProjectKey ?? undefined,
      defaultIssueType: jiraConnection.defaultIssueType ?? undefined,
    };
  }, [jiraConnection]);

  // Match results for pin manager (as Map)
  const matchResults = useMemo(() => {
    const map = new Map<string, import('./lib/types').MatchResult>();
    commentsWithMatches.forEach(cwm => {
      if (cwm.matchResult !== null) {
        map.set(cwm.comment.id, cwm.matchResult);
      }
    });
    return map;
  }, [commentsWithMatches]);

  // Expand a comment thread - used by deep links, sidebar clicks, and pin clicks
  const expandComment = useCallback((commentId: string) => {
    setExpandedCommentId(commentId);
    setSelectedCommentId(commentId);

    // Find the pin or badge element by comment ID
    // IMPORTANT: SDK uses Shadow DOM, so we must query from shadow root, not document
    const findAndSetReference = (attempt = 1) => {
      // Get shadow root from container ref
      const shadowRoot = containerRef.current?.getRootNode() as ShadowRoot | null;

      // First, try to find an individual pin with data-comment-id (for 1-3 threads)
      let pinElement = shadowRoot?.querySelector(`[data-comment-id="${commentId}"]`) as HTMLElement | null;

      if (pinElement) {
        threadRefs.setReference(pinElement);
        return;
      }

      // If no individual pin found, check if comment is in a grouped badge (4+ threads)
      const matchResult = matchResults.get(commentId);
      if (matchResult?.element) {
        const elementId = getElementId(matchResult.element);
        const badgeElement = shadowRoot?.querySelector(`[data-element-id="${elementId}"]`) as HTMLElement | null;

        if (badgeElement) {
          threadRefs.setReference(badgeElement);
          return;
        }
      }

      // Retry up to 5 times with increasing delays
      if (attempt < 5) {
        setTimeout(() => findAndSetReference(attempt + 1), 100 * attempt);
      } else {
        // After all retries, element not found - clear expanded state to prevent toggle collision
        console.warn('[Multidev Collaboration] Pin/badge element not found after 5 attempts for comment:', commentId);
        setExpandedCommentId(null);
      }
    };

    // Start looking for pin/badge after initial render
    requestAnimationFrame(() => findAndSetReference(1));

    // Clear any previous highlight since we successfully navigated to the element
    setHighlightedCommentId(null);
    // Mark as read (optimistic + fire-and-forget)
    markAsRead(commentId);
    // Fetch Jira ticket for the comment
    fetchJiraTicketForComment(commentId);
  }, [threadRefs, markAsRead, fetchJiraTicketForComment, matchResults]);

  // Handle click on a comment in the sidebar
  const handleSidebarCommentClick = useCallback((comment: Comment) => {
    // If comment is on current page, scroll to it first
    if (comment.pagePath === currentPagePath) {
      const match = commentsWithMatches.find(cwm => cwm.comment.id === comment.id);
      if (match?.matchResult?.element) {
        match.matchResult.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    // Expand the comment thread (handles Floating UI reference, mark as read, etc.)
    expandComment(comment.id);
  }, [currentPagePath, commentsWithMatches, expandComment]);

  const handleOpenSidebarWithComment = useCallback((commentId: string) => {
    setSidebarOpenWithViewportPush(true);
    setSelectedCommentId(commentId);
    setHighlightedCommentId(commentId);
  }, [setSidebarOpenWithViewportPush]);

  useDeepLinkNavigation({
    comments,
    matchResults,
    isLoading,
    onExpandComment: (_commentId, _element) => expandComment(_commentId),
    onOpenSidebarWithComment: handleOpenSidebarWithComment,
    showToast,
  });

  // Expanded comment thread
  const expandedComment = useMemo(() => {
    if (!expandedCommentId) return null;
    return comments.find(c => c.id === expandedCommentId) ?? null;
  }, [expandedCommentId, comments]);

  return (
    <div ref={containerRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {/* Floating Action Button */}
      <FAB
        isCommentModeActive={isCommentModeActive}
        onToggleCommentMode={toggleCommentMode}
        onToggleSidebar={toggleSidebar}
        commentCount={unreadCommentIds.size}
      />

      {/* Comment Mode Overlay */}
      {isCommentModeActive && (
        <CommentMode
          isActive={isCommentModeActive}
          isSidebarOpen={isSidebarOpen}
          onElementSelected={handleElementSelect}
        />
      )}

      {/* Comment Composer */}
      {selectedElement && selectedFingerprint && composerPosition && (
        <div
          ref={composerRefs.setFloating}
          style={{
            ...composerFloatingStyles,
            zIndex: 999998,
            pointerEvents: 'auto',
            opacity: isComposerPositioned ? 1 : 0,
            transition: `opacity ${tokens.animation.duration} ${tokens.animation.timing}`,
          }}
        >
          <CommentComposer
            teamMembers={teamMembers}
            onSubmit={handleSubmitComment}
            onCancel={handleCancelComposer}
          />
        </div>
      )}

      {/* Comment Pins */}
      <PinManager
        matchResults={matchResults}
        unreadCommentIds={unreadCommentIds}
        expandedCommentId={expandedCommentId ?? undefined}
        onPinClick={handlePinClick}
        comments={comments}
        authors={teamMembersMap}
        isSidebarOpen={isSidebarOpen}
      />

      {/* Expanded Comment Thread */}
      {expandedComment && (
        <div
          ref={threadRefs.setFloating}
          data-multidev-sdk="true"
          style={{
            ...threadFloatingStyles,
            zIndex: 999999,
            maxWidth: '480px',
            width: '100%',
            maxHeight: '600px',
            overflowY: 'auto',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            borderRadius: '12px',
            pointerEvents: 'auto',
            opacity: isThreadPositioned ? 1 : 0,
            transition: `opacity ${tokens.animation.duration} ${tokens.animation.timing}`,
          }}
        >
          <CommentThread
            parent={expandedComment}
            replies={getReplies(expandedComment.id)}
            authors={teamMembersMap}
            onClose={handleCloseThread}
            onCreateReply={handleCreateReply}
            onResolve={handleResolve}
            onReopen={handleReopen}
            onDeleteComment={handleDelete}
            onCopyLink={handleCopyLink}
            onMarkAsRead={(comment) => markAsRead(comment.id)}
            onMarkAsUnread={(comment) => markAsUnread(comment.id)}
            // Jira integration
            linkedTicket={expandedCommentTicket ? {
              key: expandedCommentTicket.jiraIssueKey,
              id: expandedCommentTicket.id,
              url: expandedCommentTicket.jiraIssueUrl,
              projectKey: expandedCommentTicket.jiraProjectKey,
              issueType: 'Bug',
              createdAt: expandedCommentTicket.createdAt,
              createdBy: expandedCommentTicket.createdBy,
            } : undefined}
            jiraConnection={adaptedJiraConnection}
            jiraProjects={jiraProjects}
            jiraIssueTypes={jiraIssueTypes}
            isJiraMetadataLoading={jiraProjectsLoading}
            isLoadingJiraTicket={isLoadingJiraTicket}
            onGenerateJiraTicket={handleGenerateJiraTicket}
            onCreateJiraTicket={handleCreateJiraTicket}
            onOpenJiraTicket={handleOpenJiraTicket}
            onConnectJira={connectJira}
            onFetchJiraProjects={fetchJiraProjects}
            onFetchJiraIssueTypes={fetchJiraIssueTypes}
          />
        </div>
      )}

      {/* Sidebar */}
      <Sidebar
        comments={comments}
        authors={teamMembersMap}
        currentPagePath={currentPagePath}
        isOpen={isSidebarOpen}
        selectedCommentId={selectedCommentId ?? undefined}
        highlightedCommentId={highlightedCommentId ?? undefined}
        orphanedCommentIds={orphanedCommentIds}
        unreadCommentIds={unreadCommentIds}
        hasNewComments={hasNewComments}
        isLoading={isLoading}
        onToggle={toggleSidebar}
        onCommentClick={handleSidebarCommentClick}
        onReAnchor={handleReAnchor}
        onResolve={handleResolve}
        onMarkAsRead={(comment) => markAsRead(comment.id)}
        onMarkAsUnread={(comment) => markAsUnread(comment.id)}
        onCopyLink={handleCopyLink}
        onRefresh={refreshComments}
      />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} isSidebarOpen={isSidebarOpen} />
    </div>
  );
};

/**
 * Main app component with provider wrapper
 */
export const MultidevCollaborationApp: React.FC<MultidevCollaborationAppProps> = ({ config }) => {
  // Check if auth is disabled via environment variable
  const authDisabled = import.meta.env.VITE_DISABLE_AUTH === 'true';

  if (authDisabled) {
    // Skip authentication entirely - useful during development
    return (
      <MultidevCollaborationProvider config={config}>
        <MultidevCollaborationAppInnerWithoutAuth />
      </MultidevCollaborationProvider>
    );
  }

  return (
    <AuthManagerProvider config={{
      siteId: config.siteId,
      environmentId: config.environmentId,
      apiUrl: config.apiUrl,
    }}>
      <MultidevCollaborationProvider config={config}>
        <MultidevCollaborationAppInner />
      </MultidevCollaborationProvider>
    </AuthManagerProvider>
  );
};
